import { useMutation, useQueryClient } from '@tanstack/react-query';
import { clientService } from '@services/client';
import { toast } from 'react-toastify';
import { getErrorMessage } from '@utils/errorMessage';
import { useTranslation } from '@hooks/useTranslation';
import { ClientAccessSettingKey } from '@models/client.model';
import { CLIENT_QUERY_KEY } from './useClient';

// Sprint 25, story-128 (run 5). One mutation per toggle, per the UX doc's "toggles save
// immediately" interaction pattern. No optimistic update: the toggle's checked state is read
// straight off `client.accessSettings` (query data), so a failed mutation never advances the
// cache and the switch is simply still showing its pre-toggle value — the UX doc's "reverts on
// error" with no extra bookkeeping.
export const useUpdateClientAccessSetting = () => {
  const tActionMessages = useTranslation('actionMessages');
  const queryClient = useQueryClient();
  const tClients = useTranslation('clients');

  return useMutation({
    mutationKey: ['updateClientAccessSetting'],
    mutationFn: async ({
      clientId,
      key,
      value,
    }: {
      clientId: string;
      key: ClientAccessSettingKey;
      value: boolean;
    }) => clientService.updateClientAccessSetting(clientId, key, value),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: CLIENT_QUERY_KEY(variables.clientId) });
      toast.success(tClients['accessUpdated'] || tActionMessages.clientAccessUpdated);
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, tActionMessages.requestError));
    },
  });
};

export default useUpdateClientAccessSetting;
